import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_BASE } from '../api';
import { useAuthContext } from '../context/AuthContext';

function LicenseActivationPage() {
  const { login } = useAuthContext();
  const location = useLocation();
  const navigate = useNavigate();
  const [appKey, setAppKey] = useState('');
  const [licenseKey, setLicenseKey] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isActivating, setIsActivating] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [isAccepted, setIsAccepted] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [copyFeedback, setCopyFeedback] = useState('');

  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      setIsLoading(true);
      try {
        const statusResponse = await fetch(`${API_BASE}/license/status`);
        const statusData = await statusResponse.json().catch(() => ({ valid: false }));
        if (mounted && statusData.valid) {
          navigate('/login', { replace: true });
          return;
        }

        const appKeyResponse = await fetch(`${API_BASE}/license/appkey`);
        const appKeyData = await appKeyResponse.json().catch(() => ({ app_key: '' }));
        if (mounted) {
          setAppKey(appKeyData.app_key || 'Unable to generate App Key.');
        }
      } catch {
        if (mounted) {
          setAppKey('Unable to generate App Key.');
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    loadData();
    return () => {
      mounted = false;
    };
  }, [navigate]);

  const copyAppKey = async () => {
    try {
      await navigator.clipboard.writeText(appKey);
      setCopyFeedback('App Key copied.');
    } catch {
      setCopyFeedback('Clipboard access failed. Copy manually.');
    }
  };

  const activateLicense = async () => {
    const trimmedKey = licenseKey.trim();
    if (!trimmedKey) {
      setFeedback('Paste a license key before activating.');
      return;
    }

    setIsActivating(true);
    setFeedback('');

    try {
      const response = await fetch(`${API_BASE}/license/activate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ license_key: trimmedKey }),
      });

      const data = await response.json().catch(() => ({ valid: false, reason: 'Unable to activate license.' }));
      if (!data.valid) {
        setFeedback(data.reason || 'License activation failed.');
        return;
      }

      setIsAccepted(true);
      setFeedback('License accepted successfully.');
    } catch {
      setFeedback('Unable to activate license right now.');
    } finally {
      setIsActivating(false);
    }
  };

  const continueToDashboard = async () => {
    const pendingEmail = location.state?.pendingEmail;
    const pendingPassword = location.state?.pendingPassword;
    const pendingCaptchaToken = location.state?.pendingCaptchaToken ?? null;

    if (!pendingEmail || !pendingPassword) {
      navigate('/login', { replace: true });
      return;
    }

    setIsFinalizing(true);
    setFeedback('');

    try {
      await login(pendingEmail, pendingPassword, pendingCaptchaToken);
      navigate('/dashboard', { replace: true });
    } catch (error) {
      setFeedback('License accepted. Please log in again to continue.');
      navigate('/login', { replace: true });
    } finally {
      setIsFinalizing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center px-6">
        <div className="w-full max-w-md rounded-3xl border border-white/10 bg-white/5 p-8 shadow-2xl backdrop-blur">
          <p className="text-xs uppercase tracking-[0.35em] text-cyan-300">Marizan Fitness</p>
          <h1 className="mt-4 text-3xl font-semibold">Loading activation</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-hidden bg-[radial-gradient(circle_at_top,_rgba(34,197,94,0.22),_transparent_28%),linear-gradient(135deg,_#020617_0%,_#111827_45%,_#1e293b_100%)] text-slate-100">
      <div className="mx-auto flex min-h-screen max-w-6xl items-center px-6 py-10">
        <div className="grid w-full gap-8 lg:grid-cols-[1.05fr_0.95fr]">
          <section className="rounded-[32px] border border-white/10 bg-white/6 p-8 shadow-2xl backdrop-blur-xl lg:p-12">
            <p className="text-xs uppercase tracking-[0.45em] text-emerald-300">Software License</p>
            <h1 className="mt-5 max-w-xl text-4xl font-semibold leading-tight text-white lg:text-5xl">
              Activate this server before Marizan Fitness can go live.
            </h1>
            <div className="mt-10 rounded-3xl border border-white/10 bg-slate-950/70 p-5 shadow-inner shadow-black/30">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-slate-200">App Key</p>
                </div>
                <button
                  type="button"
                  onClick={copyAppKey}
                  className="rounded-full border border-emerald-400/35 bg-emerald-400/10 px-4 py-2 text-sm font-medium text-emerald-200 transition hover:bg-emerald-400/20"
                >
                  Copy
                </button>
              </div>
              <textarea
                readOnly
                value={appKey}
                className="mt-4 h-32 w-full resize-none rounded-2xl border border-white/10 bg-slate-900/90 p-4 font-mono text-sm leading-6 text-cyan-100 outline-none"
              />
              {copyFeedback ? <p className="mt-3 text-sm text-emerald-300">{copyFeedback}</p> : null}
            </div>
          </section>

          <section className="rounded-[32px] border border-white/10 bg-slate-950/70 p-8 shadow-2xl backdrop-blur-xl lg:p-10">
            <p className="text-sm font-medium uppercase tracking-[0.3em] text-slate-400">License activation</p>
            <label className="mt-6 block text-sm font-medium text-slate-200" htmlFor="license-key">
              License Key
            </label>
            <textarea
              id="license-key"
              value={licenseKey}
              onChange={(event) => setLicenseKey(event.target.value)}
              placeholder="Paste the base64-encoded license here"
              className="mt-3 h-72 w-full resize-none rounded-3xl border border-white/10 bg-slate-900/90 p-5 text-sm leading-6 text-slate-100 outline-none transition focus:border-emerald-400/60 focus:ring-2 focus:ring-emerald-400/20"
            />
            <button
              type="button"
              onClick={activateLicense}
              disabled={isActivating || isAccepted}
              className="mt-6 w-full rounded-2xl bg-emerald-400 px-5 py-4 text-sm font-semibold text-slate-950 transition hover:bg-emerald-300 disabled:cursor-not-allowed disabled:bg-emerald-900 disabled:text-emerald-200"
            >
              {isAccepted ? 'License Accepted' : (isActivating ? 'Activating...' : 'Activate')}
            </button>
            {isAccepted ? (
              <button
                type="button"
                onClick={continueToDashboard}
                disabled={isFinalizing}
                className="mt-3 w-full rounded-2xl border border-emerald-300/60 bg-emerald-900/40 px-5 py-4 text-sm font-semibold text-emerald-100 transition hover:bg-emerald-900/60 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {isFinalizing ? 'Opening dashboard...' : 'Continue to dashboard'}
              </button>
            ) : null}
            {feedback ? (
              <p className={`mt-4 text-sm ${feedback.toLowerCase().includes('success') ? 'text-emerald-300' : 'text-rose-300'}`}>
                {feedback}
              </p>
            ) : null}
          </section>
        </div>
      </div>
    </div>
  );
}

export default LicenseActivationPage;